// src/scripts/fluco-lib/events.js
var EVENTS = {
  SHOPIFY_BLOCK_SELECT: "shopify:block:select",
  SHOPIFY_BLOCK_DESELECT: "shopify:block:deselect",
  SHOPIFY_SECTION_DESELECT: "shopify:section:deselect",
  SHOPIFY_SECTION_SELECT: "shopify:section:select",
  SHOPIFY_SECTION_LOAD: "shopify:section:load",
  SHOPIFY_SECTION_UNLOAD: "shopify:section:unload",
  COUNTDOWN_TIMER_COMPLETED_EVENT: "fluco_section:countdown_timer_completed"
};
var events_default = EVENTS;

// src/scripts/fluco-lib/media-queries.js
function getMediaQuery(querySize, sectionElement) {
  const value = getComputedStyle(sectionElement).getPropertyValue(
    `--fluco-js-${querySize}`
  );
  if (!value) {
    console.warn("Invalid querySize passed to getMediaQuery");
    return false;
  }
  return value;
}
function checkMediaQuery(querySize, sectionElement) {
  const mediaQuery = getMediaQuery(querySize, sectionElement);
  return window.matchMedia(mediaQuery).matches;
}

// src/scripts/sections/collapsible-row-tabs.js
var { SHOPIFY_BLOCK_SELECT, SHOPIFY_BLOCK_DESELECT } = events_default;
var CollapsibleRowTabs = class extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      tabsParent: "[js-tabs-parent]",
      tab: "[js-tab]",
      rowsParent: "[js-rows-parent]",
      rowGroup: "fluco-collapsible-row-group"
    };
  }
  connectedCallback() {
    this.tabsParent = this.querySelector(this.selectors.tabsParent);
    this.tabs = this.tabsParent.querySelectorAll(this.selectors.tab);
    this.rowsParent = this.querySelector(this.selectors.rowsParent);
    this.rowGroups = this.rowsParent.querySelectorAll(this.selectors.rowGroup);
    this.enableStickyTabs = this.tabsParent?.getAttribute("data-enable-sticky-tabs") === "true";
    this.contentPaddingMobileVarNum = getComputedStyle(this.parentElement).getPropertyValue("--fluco-content-area-padding-mobile").match(/([0-9.]+)/g)[0];
    this.lastCheckedY = window.scrollY;
    this.eventController = new AbortController();
    this.eventOptions = { signal: this.eventController.signal };
    this.rowGroupsVisibilityMap = Object.fromEntries(
      new Map(
        this.rowGroups.forEach((group) => {
          return [group.getAttribute("data-block-id"), false];
        })
      )
    );
    this.tabs.forEach((tab) => {
      tab.addEventListener(
        "click",
        (e) => this.handleTabClicked(e.currentTarget.getAttribute("data-block-id")),
        this.eventOptions
      );
    });
    if (this.enableStickyTabs) {
      this.viewportResizeObserver = this.createViewportResizeObserver();
      this.rowGroupIntersectionObserver = this.createRowGroupIntersectionObserver();
    }
    this.parentElement.addEventListener(
      SHOPIFY_BLOCK_SELECT,
      (e) => this.handleBlockSelected(e.detail.blockId),
      this.eventOptions
    );
    this.parentElement.addEventListener(
      SHOPIFY_BLOCK_DESELECT,
      (e) => this.handleBlockDeselected(e.detail.blockId),
      this.eventOptions
    );
  }
  disconnectedCallback() {
    this.eventController.abort();
    this.rowGroupIntersectionObserver?.disconnect();
    this.viewportResizeObserver?.disconnect();
  }
  /**
   * Handles when a block is selected.
   * @param {String} blockId - The block ID for the selected block.
   */
  handleBlockSelected(blockId) {
    this.openRow(blockId);
  }
  /**
   * Handles when a block is deselected.
   * @param {String} blockId - The block ID for the selected block.
   */
  handleBlockDeselected(blockId) {
    this.closeRow(blockId);
  }
  /**
   * Creates a resize observer
   * @returns {object}
   */
  createViewportResizeObserver() {
    const observer = new ResizeObserver(this.handleViewportResize.bind(this));
    observer.observe(document.documentElement);
    return observer;
  }
  /**
   * Creates an intersection observer, which is tuned for row group observation.
   * @returns {object}
   */
  createRowGroupIntersectionObserver() {
    const observer = new IntersectionObserver(
      this.handleRowGroupIntersect.bind(this),
      {
        rootMargin: this.getRootMargin(),
        threshold: [0.25, 0.75]
      }
    );
    this.rowGroups.forEach((rowGroup) => {
      observer.observe(rowGroup);
    });
    return observer;
  }
  /**
   * Handles when a tab is clicked.
   * @param {String} blockId - The block ID for the selected block.
   */
  handleTabClicked(blockId) {
    this.scrollToRowGroup(blockId);
  }
  /**
   * Handles when a row group is intersected.
   * @param {object} entries - The intersection observer's entries.
   */
  handleRowGroupIntersect(entries) {
    const scrollDirection = this.checkScrollDirection();
    entries.forEach((entry) => {
      const blockId = entry.target.getAttribute("data-block-id");
      let visible;
      switch (scrollDirection) {
        case "up":
          visible = entry.intersectionRatio >= 0.75;
          break;
        case "down":
          visible = entry.intersectionRatio >= 0.25;
          break;
      }
      this.rowGroupsVisibilityMap[blockId] = visible;
    });
    const firstVisibleHeaderId = Object.keys(this.rowGroupsVisibilityMap).find(
      (key) => this.rowGroupsVisibilityMap[key] === true
    );
    firstVisibleHeaderId && this.setActiveTab(firstVisibleHeaderId);
  }
  /**
   * Checks the scroll direction, and returns the result.
   * @returns {string}
   */
  checkScrollDirection() {
    const currentY = window.scrollY;
    const scrollDirection = currentY < this.lastCheckedY ? "up" : "down";
    this.lastCheckedY = currentY;
    return scrollDirection;
  }
  /**
   * Gets the current screen media's vertical offset CSS vars, and returns it as a rootMargin string.
   * @returns {string}
   */
  getRootMargin() {
    let verticalOffset;
    const computedStyle = getComputedStyle(this);
    if (checkMediaQuery("mobile", this)) {
      verticalOffset = this.getVerticalOffset("mobile", computedStyle);
    } else if (checkMediaQuery("tablet", this)) {
      verticalOffset = this.getVerticalOffset("tablet", computedStyle);
    } else {
      verticalOffset = this.getVerticalOffset("desktop", computedStyle);
    }
    return `${verticalOffset} 0px 0px 0px`;
  }
  /**
   * Checks the given screen media's vertical offset, combines that with the custom CSS offset value, and returns the complete result.
   * @returns {string}
   */
  getVerticalOffset(currentScreenMedia, computedStyle) {
    if (!computedStyle.getPropertyValue(
      `--tabs-${currentScreenMedia}-vertical-offset`
    ))
      return "0px";
    const verticalOffset = computedStyle.getPropertyValue(`--tabs-${currentScreenMedia}-vertical-offset`).match(/([0-9.]+)/g)[0];
    const customCSSOffsetValue = this.getCustomCSSOffsetValue();
    return `-${Number(verticalOffset) + Number(customCSSOffsetValue)}px`;
  }
  /**
   * Checks the  custom CSS offset value, and returns the value.
   * @returns {string}
   */
  getCustomCSSOffsetValue() {
    const computedStyleProperty = getComputedStyle(this).getPropertyValue(
      `--tabs-custom-css-variable`
    );
    if (!computedStyleProperty) return 0;
    const customCSSVariableValue = computedStyleProperty.match(/([0-9.]+)/g)[0];
    return customCSSVariableValue;
  }
  /**
   * Handles when the viewport is resized.
   */
  handleViewportResize() {
    this.isMobile = checkMediaQuery("mobile", this);
    if (this.enableStickyTabs) {
      this.rowGroupIntersectionObserver?.disconnect();
      this.rowGroupIntersectionObserver = this.createRowGroupIntersectionObserver();
    }
  }
  /**
   * Switches which tab shows the active state.
   * @param {String} blockId - The block ID corresponding to the active block.
   */
  setActiveTab(blockId) {
    this.tabs.forEach((tab) => {
      tab.toggleAttribute(
        "data-active",
        blockId === tab.getAttribute("data-block-id")
      );
      if (tab.hasAttribute("data-active")) {
        this.scrollToTabMobile(tab.getAttribute("data-block-id"));
      }
    });
  }
  /**
   * Scrolls the viewport to a row group.
   * @param {String} blockId - The block ID of the row group to scroll to.
   */
  scrollToRowGroup(blockId) {
    this.rowsParent.querySelector(`[data-block-id="${blockId}"]`).scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  }
  /**
   * Scrolls the tab container to a given tab.
   * @param {String} blockId - The block ID of the tab to scroll to.
   */
  scrollToTabMobile(blockId) {
    if (!this.isMobile) return;
    const tabEl = this.tabsParent.querySelector(`[data-block-id="${blockId}"]`);
    const tabElDOMRect = tabEl.getBoundingClientRect();
    if (tabElDOMRect.left < this.contentPaddingMobileVarNum || tabElDOMRect.right > window.innerWidth - this.contentPaddingMobileVarNum) {
      const distanceToScroll = tabEl.offsetLeft - this.contentPaddingMobileVarNum;
      tabEl.parentElement.scroll({
        left: distanceToScroll,
        behavior: "smooth"
      });
    }
  }
  /**
   * Checks if the target element is a row, then opens the row.
   * @param {String} blockId - The block ID for the selected block.
   */
  openRow(blockId) {
    const targetRow = this.rowsParent.querySelector(
      `[data-block-id="${blockId}"]`
    );
    if (targetRow.tagName === "DETAILS") targetRow.setAttribute("open", true);
  }
  /**
   * Checks if the target element is a row, then closes the row.
   * @param {String} blockId - The block ID for the selected block.
   */
  closeRow(blockId) {
    const targetRow = this.rowsParent.querySelector(
      `[data-block-id="${blockId}"]`
    );
    if (targetRow.tagName === "DETAILS") targetRow.removeAttribute("open");
  }
};
if (!customElements.get("fluco-collapsible-row-tabs")) {
  customElements.define("fluco-collapsible-row-tabs", CollapsibleRowTabs);
}
